import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { fmtNum } from "../lib/format";
import { downloadFile } from "../lib/api";
import { Button } from "../components/ui/button";
import { FileSpreadsheet } from "lucide-react";
import { useAuth } from "../lib/auth";
import ExportButtons from "../components/ExportButtons";
import PrintFrame from "../components/PrintFrame";
import { useI18n } from "../lib/i18n";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import AssetConsumablesConsumption from "../components/AssetConsumablesConsumption";

export default function Consumption() {
  const { user } = useAuth();
  const { t } = useI18n();
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(`${new Date().toISOString().slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useState(today);
  const [rows, setRows] = useState([]);
  const [outerTab, setOuterTab] = useState("linen");
  const rangeQ = `date_from=${dateFrom}&date_to=${dateTo}`;

  useEffect(() => {
    api.get(`/reports/monthly-consumption?${rangeQ}`).then(({ data }) => setRows(data.rows || []));
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = rows.reduce((acc, r) => {
    ["opening","purchases","room_issues","laundry_sent","laundry_returned","missing_qty","damaged_qty","disposal_loss","baseline_adjustment","adjustments","closing"].forEach(k => {
      acc[k] = (acc[k] || 0) + (r[k] || 0);
    });
    return acc;
  }, {});

  return (
    <Layout>
      <PrintFrame title={`Consumption Report — ${dateFrom === dateTo ? dateFrom : `${dateFrom} to ${dateTo}`}`} subtitle="Operational movements clearly separate from approved consumption." user={user}>
        <div className="mb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-3 no-print">
          <div>
            <h1 className="section-title" data-testid="page-title">{t("nav.consumption")}</h1>
            <div className="section-sub">{t("sub.consumption")}</div>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div><Label>From</Label><Input type="date" value={dateFrom} max={dateTo} onChange={e => setDateFrom(e.target.value)} className="h-10 mt-1" data-testid="cons-date-from" /></div>
            <div><Label>To</Label><Input type="date" value={dateTo} min={dateFrom} max={today} onChange={e => setDateTo(e.target.value)} className="h-10 mt-1" data-testid="cons-date-to" /></div>
            {outerTab === "linen"
              ? <ExportButtons endpoint="/reports/export/monthly-consumption" filenameBase={`linen-consumption-${dateFrom}_${dateTo}`} extraQuery={rangeQ} />
              : <ExportButtons endpoint="/assets/reports/export/consumables-consumption" filenameBase={`consumables-consumption-${dateFrom}_${dateTo}`} extraQuery={rangeQ} />}
            <Button variant="outline" size="sm" className="h-9 border-slate-900 text-slate-900"
              onClick={() => downloadFile(`/assets/reports/export/monthly-complete?format=xlsx&${rangeQ}`, `consumption-complete-${dateFrom}_${dateTo}.xlsx`)}
              data-testid="export-complete-report">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export Complete Report
            </Button>
          </div>
        </div>

        <Tabs value={outerTab} onValueChange={setOuterTab} className="mb-4">
          <TabsList data-testid="cons-outer-tabs" className="no-print">
            <TabsTrigger value="linen" data-testid="cons-tab-linen">Linen Consumption</TabsTrigger>
            <TabsTrigger value="consumables" data-testid="cons-tab-consumables">Consumables Consumption</TabsTrigger>
          </TabsList>
          <TabsContent value="linen">

        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="vr-table" data-testid="consumption-table">
              <thead><tr>
                <th>Item</th>
                <th className="num">Opening</th><th className="num">Purchases</th><th className="num">Room Issues</th>
                <th className="num">Laundry Sent</th><th className="num">Laundry Returned</th>
                <th className="num">Missing</th><th className="num">Damaged</th><th className="num">Disposal / Loss</th>
                <th className="num">Baseline Adjustment</th>
                <th className="num">Closing</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.item_id}>
                    <td>{r.name}</td>
                    <td className="num">{fmtNum(r.opening)}</td>
                    <td className="num">{fmtNum(r.purchases)}</td>
                    <td className="num">{fmtNum(r.room_issues)}</td>
                    <td className="num">{fmtNum(r.laundry_sent)}</td>
                    <td className="num">{fmtNum(r.laundry_returned)}</td>
                    <td className="num">{fmtNum(r.missing_qty)}</td>
                    <td className="num">{fmtNum(r.damaged_qty)}</td>
                    <td className="num font-semibold text-red-700">{fmtNum(r.disposal_loss)}</td>
                    <td className="num text-amber-800">{fmtNum(r.baseline_adjustment)}</td>
                    <td className="num font-semibold">{fmtNum(r.closing)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="bg-slate-50 font-semibold">
                <td colSpan={1}>Totals</td>
                <td className="num">{fmtNum(totals.opening)}</td>
                <td className="num">{fmtNum(totals.purchases)}</td>
                <td className="num">{fmtNum(totals.room_issues)}</td>
                <td className="num">{fmtNum(totals.laundry_sent)}</td>
                <td className="num">{fmtNum(totals.laundry_returned)}</td>
                <td className="num">{fmtNum(totals.missing_qty)}</td>
                <td className="num">{fmtNum(totals.damaged_qty)}</td>
                <td className="num">{fmtNum(totals.disposal_loss)}</td>
                <td className="num">{fmtNum(totals.baseline_adjustment)}</td>
                <td className="num">{fmtNum(totals.closing)}</td>
              </tr></tfoot>
            </table>
          </div>
        </Card>
          </TabsContent>
          <TabsContent value="consumables">
            <AssetConsumablesConsumption dateFrom={dateFrom} dateTo={dateTo} />
          </TabsContent>
        </Tabs>
      </PrintFrame>
    </Layout>
  );
}
